import React from 'react';
import clsx from 'clsx';
import type {SetOptional} from 'type-fest';
import {type IconDefinition} from '@fortawesome/fontawesome-common-types';
import {
	type AlertColor,
	Dialog as MuiDialog,
	type DialogProps as MuiDialogProps,
	DialogContent as MuiDialogContent,
	DialogActions as MuiDialogActions,
	dialogClasses as muiDialogClasses,
	type DialogActionsProps as MuiDialogActionsProps,
	type DialogContentProps as MuiDialogContentProps,
	LinearProgress,
	Slide,
	type SlideProps,
} from '@mui/material';
import useMediaQuery from '@netx/core-components/hooks/useMediaQuery';
import {DialogAlert, DialogAlertProps} from '@netx/core-components/DialogAlert';
import {
	DialogTitle,
	type DialogTitleProps,
} from '@netx/core-components/DialogTitle';
import {
	composeClasses,
	styled,
	useTheme,
	type WithMuiPropOverrides,
	type WithClasses,
} from '@netx/core-components/Theme';
import {splitEventHandler} from '@netx/core-next/utils';
import {
	dialogClasses,
	getDialogUtilityClass,
	DIALOG_CLASS_NAME,
	type DialogClasses,
} from './dialogClasses';

export type DialogOwnerState = WithClasses<
	DialogClasses,
	{
		fullSize?: boolean;
		fullScreenWhenMobile?: boolean;
		disableContentPadding?: boolean;
	}
>;

const useUtilityClasses = (ownerState: DialogOwnerState) => {
	const {classes, disableContentPadding, fullSize} = ownerState;
	const slots = {
		root: ['root', fullSize && 'fullSize'],
		content: ['content', disableContentPadding && 'disableContentPadding'],
		actions: ['actions'],
	};
	return composeClasses(slots, getDialogUtilityClass, classes);
};

// TODO: this is a hack until we can find a better way to manage when and when not to show the main app
// upload dropzone
// we already were doing a check for any modals (backbone) being open, so just continuing that behavior for now...
export let dialogsOpenCount = 0;

const DialogRoot = styled(MuiDialog, {
	name: DIALOG_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => {
	const {fullSize} = ownerState as DialogOwnerState;
	const classes = {
		...(fullSize && {
			[`& .${muiDialogClasses.paper}:not(.${muiDialogClasses.paperFullScreen})`]:
				{
					height: 'calc(100% - 64px)',
					maxWidth: 'initial',
					width: 'calc(100% - 64px)',
				},
		}),
	};
	return classes;
});

const DialogActions = styled(MuiDialogActions, {
	name: DIALOG_CLASS_NAME,
	slot: 'Actions',
})(({theme}) => ({
	padding: theme.spacing(2),
}));

const DialogContent = styled(MuiDialogContent, {
	name: DIALOG_CLASS_NAME,
	slot: 'Content',
})(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	position: 'relative',
	[`&.${dialogClasses.disableContentPadding}`]: {
		padding: 0,
	},
}));

const DialogProgress = styled(LinearProgress, {
	name: DIALOG_CLASS_NAME,
	slot: 'Progress',
})(({theme}) => ({
	...theme.mixins.absoluteFill,
	bottom: 'initial',
}));

const Transition = React.forwardRef<HTMLElement, SlideProps>(
	function Transition(props, ref) {
		return <Slide direction="up" ref={ref} {...props} />;
	},
);

export interface DialogProps
	extends Omit<MuiDialogProps, 'classes' | 'title'>,
		WithClasses<DialogClasses> {
	// TODO: why missing?
	'data-testid'?: string;
	dialogActions?: React.ReactNode;
	disableContentPadding?: boolean;
	disabled?: boolean;
	error?: React.ReactNode;
	fullSize?: boolean;
	fullScreenWhenMobile?: boolean;
	isWorking?: boolean;
	severity?: AlertColor;
	title?: React.ReactNode;
	secondaryTitle?: React.ReactNode;
	loading?: boolean;
	showSecondaryTitleSkeletonWhileLoading?: boolean;
	icon?: IconDefinition;
	controls?: React.ReactNode;
	preventBackdropClose?: boolean;
	progress?: number;
	DialogActionsProps?: Partial<MuiDialogActionsProps>;
	DialogAlertProps?: Partial<DialogAlertProps>;
	DialogContentProps?: Partial<MuiDialogContentProps>;
	DialogTitleProps?: Partial<DialogTitleProps>;
}

export type WithDialogProps<P = {}> = P &
	Pick<DialogProps, 'open' | 'onClose'> & {
		DialogProps?: SetOptional<Omit<DialogProps, 'open' | 'onClose'>, 'title'>;
	};

/**
 * NetX Dialog
 * Provides a consistent animation/experience
 */
const DialogPrimitive = React.forwardRef<HTMLDivElement, DialogProps>(
	(
		{
			children,
			classes: _classes,
			className,
			controls,
			dialogActions,
			disableContentPadding,
			disabled,
			error,
			fullSize,
			fullScreen: _fullScreen,
			fullScreenWhenMobile,
			icon,
			isWorking,
			loading,
			progress,
			preventBackdropClose,
			onClose,
			secondaryTitle,
			showSecondaryTitleSkeletonWhileLoading,
			// TODO: severity styling support for 'danger' / 'info' / 'warning' / 'success'
			severity = 'info',
			title,
			DialogActionsProps,
			DialogAlertProps,
			DialogContentProps,
			DialogTitleProps,
			...props
		}: DialogProps,
		ref,
	) => {
		const ownerState: DialogOwnerState = {
			classes: _classes,
			className,
			disableContentPadding,
			fullSize,
			fullScreenWhenMobile,
		};

		const classes = useUtilityClasses(ownerState);

		const theme = useTheme();

		const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

		const fullScreen = _fullScreen || (fullScreenWhenMobile && smallScreen);

		const handleClose = onClose
			? () => onClose({}, 'escapeKeyDown')
			: undefined;

		return (
			<DialogRoot
				ref={ref}
				aria-labelledby={(title != null && 'dialog-title') || undefined}
				className={clsx(classes.root, className)}
				fullScreen={fullScreen}
				ownerState={ownerState}
				onClose={(event, reason) => {
					if (preventBackdropClose && reason === 'backdropClick') return;
					onClose?.(event, reason);
				}}
				TransitionComponent={Transition}
				{...props}
				TransitionProps={{
					...props.TransitionProps,
					onEnter: splitEventHandler(
						props.TransitionProps?.onEnter,
						// see hack notes above
						() => dialogsOpenCount++,
					),
					onExit: splitEventHandler(
						props.TransitionProps?.onExit,
						// see hack notes above
						// also, never got negative - can't have negative dialogs
						() => (dialogsOpenCount = Math.max(dialogsOpenCount - 1, 0)),
					),
				}}
			>
				{isWorking ? (
					<DialogProgress
						value={progress}
						variant={progress != null ? 'determinate' : 'indeterminate'}
					/>
				) : null}

				{(title && (
					<DialogTitle
						controls={controls}
						disabled={disabled}
						id="dialog-title"
						primary={title}
						secondary={secondaryTitle}
						icon={icon}
						onClose={handleClose}
						{...DialogTitleProps}
					/>
				)) ||
					null}

				{error != null ? (
					<DialogAlert {...DialogAlertProps}>{error}</DialogAlert>
				) : null}

				<DialogContent
					{...DialogContentProps}
					className={clsx(classes.content, DialogContentProps?.className)}
				>
					{children}
				</DialogContent>

				{dialogActions && (
					<DialogActions
						{...DialogActionsProps}
						className={clsx(classes.actions, DialogActionsProps?.className)}
					>
						{dialogActions}
					</DialogActions>
				)}
			</DialogRoot>
		);
	},
);

export const Dialog = DialogPrimitive;

export default Dialog;
